import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { type OptionItem } from "@/api/admin";
import {
  type AdminTransactionItem,
  useGetAllAdminTransactionsQuery,
} from "@/api/admin/transactions";
import {
  type ITransactions,
  PaymentsMethods,
} from "@/api/restaurant/types/transactionsTypes";
import {
  Loader,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { formatPaymentMethod } from "@/modules/RestaurantTransactions/formatPaymentMethod";
import { STATUS_MAP_TRANSACTIONS_COMBINED } from "@/types/api";
import { COMPANY_NAME } from "@/utils/constants/defaultNames";
import { createSortHandler } from "@/utils/createSortHandler";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

enum TransactionsColumn {
  DATE_TIME = "Created at",
  REFERENCE_NUMBER = "Reference Number",
  ORDER_CODE = "Order Code",
  BILL_NUMBER = "Bill Number",
  NET_AMOUNT = "Net Amount",
  GROSS_AMOUNT = "Gross Amount",
  TAX_AMOUNT = "Taxes",
  TIPS = "Tips",
  TOTAL_PAYABLE = "Total payable",
  CREDIT_EARNED = "Credit earned",
  CREDIT_REDEEMED = "Credit redeemed",
  PAYMENT_METHOD = "Payment method",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TransactionsColumn);

const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
  [TransactionsColumn.DATE_TIME]: { type: "date" },
};

const AdminBranchTransactionsTable = () => {
  const { id = "" } = useParams();
  const [searchValue, setSearchValue] = useState("");

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { branchId: id, query: "" },
      },
      searchValue
    );

  const { data: branchData, isPending: isBranchPending } =
    useGetAllAdminTransactionsQuery({ ...tableParams });

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [TransactionsColumn.DATE_TIME]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const { dateFrom, dateTo } = parseDateRangeToParams(
      selectedMap[TransactionsColumn.DATE_TIME]?.[0],
      true
    );

    setTableParams(prev => ({
      ...prev,
      dateFrom,
      dateTo,
      offset: 0,
    }));
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const handleSortDate = createSortHandler(setTableParams, "createdAt");
  const handleSortGrossAmount = createSortHandler(
    setTableParams,
    "totalAmount"
  );
  const handleSortTaxsAmount = createSortHandler(setTableParams, "taxAmount");
  const handleSortTipsAmount = createSortHandler(setTableParams, "tipsAmount");
  const handleSortNetAmount = createSortHandler(setTableParams, "netAmount");
  const handleSortCashRedeemed = createSortHandler(
    setTableParams,
    "creditRedeemed"
  );
  const handleSortCashEarned = createSortHandler(
    setTableParams,
    "cashbackEarned"
  );
  const handleSortTotalPayable = createSortHandler(
    setTableParams,
    "totalPayable"
  );

  const money = useAdminMoney();

  const columns: ColumnConfig<ITransactions>[] = [
    {
      key: "createdAt",
      label: TransactionsColumn.DATE_TIME,
      visible: flags[TransactionsColumn.DATE_TIME],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortDate}
          field="createdAt"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[12%]">
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "referenceNumber",
      label: TransactionsColumn.REFERENCE_NUMBER,
      visible: flags[TransactionsColumn.REFERENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.referenceNumber.toString()}
        </TableCellText>
      ),
    },
    {
      key: "orderCode",
      label: TransactionsColumn.ORDER_CODE,
      visible: flags[TransactionsColumn.ORDER_CODE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellLink last={isLast}>
          <Link
            to={PATHS.ADMIN.ACCOUNT_BRANCH_TRANSACTIONS_DETAILS(id, row.id)}
            state={{ from: `${location.pathname}#transactions` }}
            data-testid={`cell-id-${row.id}`}
          >
            {row?.orderCode ?? "-"}
          </Link>
        </TableCellLink>
      ),
    },
    {
      key: "billNumber",
      label: TransactionsColumn.BILL_NUMBER,
      visible: flags[TransactionsColumn.BILL_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.billNumber}</TableCellText>
      ),
    },
    {
      key: "netAmount",
      label: TransactionsColumn.NET_AMOUNT,
      visible: flags[TransactionsColumn.NET_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortNetAmount}
          field="netAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.netAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "grossAmount",
      label: TransactionsColumn.GROSS_AMOUNT,
      visible: flags[TransactionsColumn.GROSS_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortGrossAmount}
          field="totalAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.grossAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "taxAmount",
      label: TransactionsColumn.TAX_AMOUNT,
      visible: flags[TransactionsColumn.TAX_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortTaxsAmount}
          field="taxAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.taxAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "tipsAmount",
      label: TransactionsColumn.TIPS,
      visible: flags[TransactionsColumn.TIPS],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortTipsAmount}
          field="tipsAmount"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.tipsAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "totalPayable",
      label: TransactionsColumn.TOTAL_PAYABLE,
      visible: flags[TransactionsColumn.TOTAL_PAYABLE],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortTotalPayable}
          field="totalPayable"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.totalPayable?.toString() ?? "-")}
        </TableCellText>
      ),
    },
    {
      key: "cashbackEarned",
      label: TransactionsColumn.CREDIT_EARNED,
      visible: flags[TransactionsColumn.CREDIT_EARNED],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortCashEarned}
          field="cashbackEarned"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.cashbackEarned)}
        </TableCellText>
      ),
    },
    {
      key: "creditRedeemed",
      label: TransactionsColumn.CREDIT_REDEEMED,
      visible: flags[TransactionsColumn.CREDIT_REDEEMED],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortCashRedeemed}
          field="creditRedeemed"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.creditRedeemed)}
        </TableCellText>
      ),
    },
    {
      key: "paymentMethod",
      label: TransactionsColumn.PAYMENT_METHOD,
      visible: flags[TransactionsColumn.PAYMENT_METHOD],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          <>
            {row?.usedWallet &&
            row?.paymentMethod !== PaymentsMethods.SELFSERVE_WALLET
              ? `${COMPANY_NAME} Wallet + `
              : ""}
            {formatPaymentMethod(row?.paymentMethod ?? "-")}
          </>
        </TableCellText>
      ),
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_MAP_TRANSACTIONS_COMBINED}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isBranchPending ? (
        <Loader />
      ) : (
        <DynamicTable<AdminTransactionItem>
          data={branchData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={branchData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AdminBranchTransactionsTable;
